import { BASE_XP_BY_GUESS, lengthMultiplier } from './lengths';
import { WORD_LENGTH } from './types';

/**
 * The 5-letter win table (spec §3.5), kept as the legacy export. The
 * generalized table is `BASE_XP_BY_GUESS` in lengths.ts, whose first six
 * entries are exactly these.
 */
export const WIN_XP: readonly number[] = [100, 80, 60, 50, 40, 30];

const LOSS_XP = 5;
const HINT_PENALTY = 10;
const STREAK_BONUS_PER_DAY = 2;
const STREAK_BONUS_CAP = 20;
const CHALLENGE_WIN_XP = 25;

/**
 * XP for a finished daily game. `streakAfter` is the streak including this
 * win; `length` defaults to 5 so every pre-ladder caller keeps its exact
 * numbers.
 *
 * The order in spec §10.1 is load-bearing: the multiplier applies to
 * `base + streakBonus`, that product is rounded once, and only then is the
 * hint penalty subtracted and the result floored at 0. A loss pays a flat
 * 5 — unmultiplied, because a hard word is not a consolation prize.
 */
export function dailyXp(
  won: boolean,
  guessCount: number,
  streakAfter: number,
  hintsUsed: number,
  length: number = WORD_LENGTH,
): number {
  if (!won) return Math.max(0, LOSS_XP - HINT_PENALTY * hintsUsed);
  const base = BASE_XP_BY_GUESS[guessCount - 1]!;
  const bonus = Math.min(STREAK_BONUS_PER_DAY * streakAfter, STREAK_BONUS_CAP);
  const scaled = Math.round(lengthMultiplier(length) * (base + bonus));
  return Math.max(0, scaled - HINT_PENALTY * hintsUsed);
}

/** XP for a finished challenge game. Challenges never affect streak. */
export function challengeXp(
  won: boolean,
  hintsUsed: number,
  length: number = WORD_LENGTH,
): number {
  if (!won) return 0;
  const scaled = Math.round(lengthMultiplier(length) * CHALLENGE_WIN_XP);
  return Math.max(0, scaled - HINT_PENALTY * hintsUsed);
}
